import * as React from "react";
import { Check, ChevronsUpDown, AlertCircle, Search } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Input } from "@/components/ui/input";
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "@/components/ui/tooltip";
import { Badge } from "@/components/ui/badge";

interface HubspotField {
    name: string;
    label: string;
    type: string;
    description: string;
    groupName: string;
    required: boolean;
    fieldType?: string;
    externalOptions?: boolean;
    options?: Array<{ value: string; label: string }>;
}

function getFieldWarningMessage(field: HubspotField): string | null {
    switch (field.type) {
        case 'enumeration':
            return "This is a HubSpot option field. Aimdoc will translate matching labels to HubSpot's internal option values during sync.";
        case 'bool':
            return "This field only accepts true/false values";
        case 'date':
            return "Date must be in YYYY-MM-DD format";
        case 'datetime':
            return "DateTime must be in ISO 8601 format";
        case 'number':
            return "This field must be a number";
        case 'string':
            return null;
        default:
            return null;
    }
}

interface HubspotFieldSelectProps {
    fields: HubspotField[];
    value?: string;
    onSelect: (value: string) => void;
    placeholder?: string;
    simpleDisplay?: boolean;
}

export function HubspotFieldSelect({ fields, value, onSelect, placeholder = "Select a field...", simpleDisplay = false }: HubspotFieldSelectProps) {
    const [open, setOpen] = React.useState(false);
    const [searchQuery, setSearchQuery] = React.useState("");
    const inputRef = React.useRef<HTMLInputElement>(null);

    const selectedField = fields.find(field => field.name === value);
    const warningMessage = selectedField && getFieldWarningMessage(selectedField);

    const filteredFields = fields.filter(field => 
        field.label.toLowerCase().includes(searchQuery.toLowerCase()) ||
        field.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        field.groupName.toLowerCase().includes(searchQuery.toLowerCase())
    );

    // Group fields by groupName for better organization
    const groupedFields = React.useMemo(() => {
        const groups: Record<string, HubspotField[]> = {};
        filteredFields.forEach(field => {
            const groupKey = field.groupName || 'Other';
            if (!groups[groupKey]) groups[groupKey] = [];
            groups[groupKey].push(field);
        });
        // Sort groups alphabetically
        const sortedGroups: Record<string, HubspotField[]> = {};
        Object.keys(groups).sort().forEach(key => {
            sortedGroups[key] = groups[key];
        });
        return sortedGroups;
    }, [filteredFields]);

    React.useEffect(() => {
        if (open && inputRef.current) {
            setTimeout(() => inputRef.current?.focus(), 0);
        }
    }, [open]);

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={open}
                    className={cn(
                        "w-full justify-between h-9 font-normal",
                        !selectedField && "text-muted-foreground"
                    )}
                >
                    {selectedField ? (
                        <div className="flex items-center gap-2 truncate">
                            <span className="truncate">{selectedField.label}</span>
                            {!simpleDisplay && (
                                <Badge variant="secondary" className="text-[10px] flex-shrink-0">
                                    {selectedField.type}
                                </Badge>
                            )}
                            {warningMessage && (
                                <TooltipProvider>
                                    <Tooltip>
                                        <TooltipTrigger asChild>
                                            <span
                                                className="inline-flex flex-shrink-0 items-center"
                                                aria-label={warningMessage}
                                            >
                                                <AlertCircle className="h-3.5 w-3.5 text-amber-500" />
                                            </span>
                                        </TooltipTrigger>
                                        <TooltipContent className="z-[70]">
                                            <div className="max-w-xs text-xs">{warningMessage}</div>
                                        </TooltipContent>
                                    </Tooltip>
                                </TooltipProvider>
                            )}
                        </div>
                    ) : (
                        <span>{placeholder}</span>
                    )}
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
            </PopoverTrigger>
            <PopoverContent 
                className="p-0 w-[--radix-popover-trigger-width]" 
                align="start" 
                sideOffset={4}
                onWheel={(e) => e.stopPropagation()}
            >
                <div className="flex flex-col">
                    <div className="p-2 border-b">
                        <div className="relative">
                            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                            <Input
                                ref={inputRef}
                                placeholder="Search properties..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="h-8 pl-8"
                            />
                        </div>
                    </div>
                    <ScrollArea className="h-[280px]">
                        {filteredFields.length === 0 ? (
                            <div className="py-6 text-center text-sm text-muted-foreground">
                                No properties found
                            </div>
                        ) : (
                            <div className="p-1">
                                {Object.entries(groupedFields).map(([groupName, groupFields]) => (
                                    <div key={groupName}>
                                        <div className="px-2 py-1.5 text-xs font-medium text-muted-foreground sticky top-0 bg-popover capitalize">
                                            {groupName} ({groupFields.length})
                                        </div>
                                        {groupFields.map((field) => (
                                            <button
                                                key={field.name}
                                                onClick={() => {
                                                    onSelect(field.name);
                                                    setOpen(false);
                                                    setSearchQuery("");
                                                }}
                                                className={cn(
                                                    "w-full flex items-center gap-2 px-2 py-2 text-left rounded-md transition-colors",
                                                    "hover:bg-accent focus:bg-accent focus:outline-none",
                                                    field.name === value && "bg-accent"
                                                )}
                                            >
                                                <div className={cn(
                                                    "h-4 w-4 rounded flex items-center justify-center flex-shrink-0",
                                                    field.name === value ? "text-primary" : "text-transparent"
                                                )}>
                                                    <Check className="h-3.5 w-3.5" />
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-center gap-2">
                                                        <span className="text-sm font-medium truncate">{field.label}</span>
                                                        {field.required && (
                                                            <Badge variant="destructive" className="text-[9px] px-1 py-0">
                                                                Required
                                                            </Badge>
                                                        )}
                                                    </div>
                                                    <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground truncate">
                                                        <span className="font-mono">{field.name}</span>
                                                        <span>•</span>
                                                        <span className="capitalize">{field.type}</span>
                                                        {field.fieldType && (
                                                            <>
                                                                <span>•</span>
                                                                <span className="capitalize">{field.fieldType}</span>
                                                            </>
                                                        )}
                                                    </div>
                                                </div>
                                            </button>
                                        ))}
                                    </div>
                                ))}
                            </div>
                        )}
                    </ScrollArea>
                </div>
            </PopoverContent>
        </Popover>
    );
}
